/**
 * Excel de verdad (.xlsx) a partir del CSV que ya generan las descargas.
 *
 * Hasta el 25-9-2026 la descarga «Excel» era un CSV con extensión .xls: Excel
 * avisaba de que el formato no coincidía y, al abrirlo, convertía en fechas
 * códigos como «75-01-4» (números CAS) o «12-2», y quitaba los ceros a la
 * izquierda de referencias como «0003.000101». Aquí cada celda lleva su tipo:
 * número solo si es un número limpio sin ceros a la izquierda; todo lo demás,
 * texto tal cual. Se empaqueta con fflate, que se carga solo al descargar.
 *
 * `rowsToXlsxBlob` (3-11-2026) escribe un libro con varias hojas a partir de
 * filas ya hechas: «Descargar el mes» de las facturas (cabeceras + líneas).
 */

function parseCsv(text) {
  const s = String(text || "").replace(/^﻿/, "");
  const rows = [];
  let row = [];
  let cell = "";
  let quoted = false;
  for (let i = 0; i < s.length; i += 1) {
    const ch = s[i];
    if (quoted) {
      if (ch === '"' && s[i + 1] === '"') {
        cell += '"';
        i += 1;
      } else if (ch === '"') quoted = false;
      else cell += ch;
    } else if (ch === '"') quoted = true;
    else if (ch === ",") {
      row.push(cell);
      cell = "";
    } else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && s[i + 1] === "\n") i += 1;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = "";
    } else cell += ch;
  }
  if (cell !== "" || row.length) {
    row.push(cell);
    rows.push(row);
  }
  return rows;
}

const isCleanNumber = (v) => /^-?(0|[1-9]\d{0,14})(\.\d+)?$/.test(v);
const xmlEsc = (v) =>
  String(v)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    // caracteres de control no válidos en XML
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, "")
    // sustitutos sueltos y U+FFFE/U+FFFF: tampoco son XML válido
    .replace(/[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(^|[^\uD800-\uDBFF])[\uDC00-\uDFFF]/g, "$1")
    .replace(/[￾￿]/g, "");

// Fecha ISO → número de serie de Excel (días desde 1899-12-30).
const excelSerial = (iso) => {
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  return Math.round((Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) - Date.UTC(1899, 11, 30)) / 86400000);
};

function colName(i) {
  let n = i + 1;
  let s = "";
  while (n > 0) {
    const m = (n - 1) % 26;
    s = String.fromCharCode(65 + m) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
}

function sheetXml(rows) {
  const body = rows
    .map((r, ri) => {
      const cells = r
        .map((v, ci) => {
          const ref = `${colName(ci)}${ri + 1}`;
          if (v === "" || v === null || v === undefined) return "";
          // { date: "AAAA-MM-DD" }: fecha de verdad (número de serie con formato de fecha).
          if (v && typeof v === "object" && "date" in v) {
            const serial = excelSerial(v.date);
            if (serial !== null) return `<c r="${ref}" s="2"><v>${serial}</v></c>`;
            v = String(v.date || "");
            if (!v) return "";
          }
          if (ri > 0 && isCleanNumber(v)) return `<c r="${ref}"><v>${v}</v></c>`;
          const style = ri === 0 ? ' s="1"' : "";
          return `<c r="${ref}" t="inlineStr"${style}><is><t xml:space="preserve">${xmlEsc(v)}</t></is></c>`;
        })
        .join("");
      return `<row r="${ri + 1}">${cells}</row>`;
    })
    .join("");
  const ncols = Math.max(1, ...rows.map((r) => r.length));
  const width = (v) => (v && typeof v === "object" ? 12 : String(v ?? "").length + 2);
  const widths = Array.from({ length: ncols }, (_, i) => {
    const w = Math.min(60, Math.max(8, ...rows.map((r) => width(r[i]))));
    return `<col min="${i + 1}" max="${i + 1}" width="${w}" customWidth="1"/>`;
  }).join("");
  return (
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
    '<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">' +
    '<sheetViews><sheetView workbookViewId="0"><pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews>' +
    `<cols>${widths}</cols><sheetData>${body}</sheetData></worksheet>`
  );
}

// Nombres de hoja válidos para Excel: ≤ 31 caracteres, sin : \ / ? * [ ] y sin repetir.
function sheetNames(names) {
  const used = new Set();
  return names.map((raw, i) => {
    const base = String(raw || "").replace(/[:\\/?*[\]]/g, " ").trim().slice(0, 31) || `Sheet${i + 1}`;
    let name = base;
    for (let n = 2; used.has(name.toLowerCase()); n += 1) name = `${base.slice(0, 27)} ${n}`;
    used.add(name.toLowerCase());
    return name;
  });
}

const XLSX_TYPE = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
const XML_HEAD = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>';
const STYLES =
  XML_HEAD +
  '<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">' +
  '<fonts count="2"><font><sz val="11"/><name val="Calibri"/></font><font><b/><sz val="11"/><name val="Calibri"/></font></fonts>' +
  '<fills count="2"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill></fills>' +
  '<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>' +
  '<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>' +
  // 0 normal, 1 cabecera en negrita, 2 fecha (formato 14: fecha corta según la configuración regional)
  '<cellXfs count="3"><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/><xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/><xf numFmtId="14" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/></cellXfs>' +
  '<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>' +
  "</styleSheet>";

// Ficheros del paquete .xlsx (sin comprimir): separado para poder probarlo sin fflate.
function workbookFiles(sheets) {
  const list = sheets && sheets.length ? sheets : [{ name: "WhappScan", rows: [] }];
  const names = sheetNames(list.map((s) => s.name));
  const text = (rows) =>
    (rows || []).map((r) =>
      (r || []).map((v) => (v === null || v === undefined ? "" : v && typeof v === "object" && "date" in v ? v : String(v)))
    );
  const files = {
    "[Content_Types].xml":
      XML_HEAD +
      '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">' +
      '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>' +
      '<Default Extension="xml" ContentType="application/xml"/>' +
      '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>' +
      list
        .map(
          (_, i) =>
            `<Override PartName="/xl/worksheets/sheet${i + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`
        )
        .join("") +
      '<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>' +
      "</Types>",
    "_rels/.rels":
      XML_HEAD +
      '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
      '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>' +
      "</Relationships>",
    "xl/workbook.xml":
      XML_HEAD +
      '<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">' +
      `<sheets>${names.map((n, i) => `<sheet name="${xmlEsc(n)}" sheetId="${i + 1}" r:id="rId${i + 1}"/>`).join("")}</sheets></workbook>`,
    "xl/_rels/workbook.xml.rels":
      XML_HEAD +
      '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
      list
        .map(
          (_, i) =>
            `<Relationship Id="rId${i + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${i + 1}.xml"/>`
        )
        .join("") +
      `<Relationship Id="rId${list.length + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>` +
      "</Relationships>",
    "xl/styles.xml": STYLES,
  };
  list.forEach((s, i) => {
    files[`xl/worksheets/sheet${i + 1}.xml`] = sheetXml(text(s.rows));
  });
  return files;
}

/** Libro con varias hojas: `sheets` = [{ name, rows }]; la primera fila de cada hoja va en negrita. */
export async function rowsToXlsxBlob(sheets) {
  const { zipSync, strToU8 } = await import("fflate");
  const files = workbookFiles(sheets);
  const bin = {};
  Object.keys(files).forEach((k) => {
    bin[k] = strToU8(files[k]);
  });
  return new Blob([zipSync(bin)], { type: XLSX_TYPE });
}

export async function csvToXlsxBlob(csv, sheetName = "WhappScan") {
  return rowsToXlsxBlob([{ name: sheetName, rows: parseCsv(csv) }]);
}

export { parseCsv, sheetNames, workbookFiles };
